/* eslint-disable no-return-assign */
import React from 'react';
import { useAtlasV2MetricByWeekId, calculatePromotionsPercentChange } from 'src/serverProxy/useAtlasV2MetricByWeekId';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { formatMetric, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2ComparableSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ComparableSplineChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { Grid } from '@mui/material';
import { ComparisonPeriod, useComparisonPeriod, useMainTimePeriod } from 'src/utils/Hooks';
import { timestampToWeekId } from 'src/utils/dateUtils';

/**
 * Shows donut chart and spline charts for the top section Promotions
 */
export const AtlasV2PromotionsTopSection = () => {
  const { id: mainTimePeriodId, startWeek } = useMainTimePeriod();
  const comparisonPeriod = useComparisonPeriod();

  const { primaryTotal: retailSales, isLoading: retailSalesLoading } = useAtlasV2MetricByWeekId({
    indexName: 'sales',
    fieldName: 'retailSales'
  });
  const {
    primaryTotal: retailSalesFromPromotions,
    isLoading: retailSalesFromPromotionsLoading,
    primarySlicedData: retailSalesFromPromotionSlicedData
  } = useAtlasV2MetricByWeekId({
    indexName: 'promotions',
    fieldName: 'retailSales'
  });

  const isLastWeek = mainTimePeriodId === '1w';

  const lastWeekRetailSalesFromPromotions =
    retailSalesFromPromotionSlicedData?.[retailSalesFromPromotionSlicedData.length - 1]?.[0] ===
      timestampToWeekId(startWeek) || 0;
  const percentage = safeDivide(
    isLastWeek ? lastWeekRetailSalesFromPromotions : retailSalesFromPromotions,
    retailSales,
    0
  );

  return (
    <BeaconChartWithLegend title="Promotion Count" includeSummaryInfo showExportMenu={false}>
      <Grid display="grid" gridTemplateColumns="308px 655px" justifyContent="space-between" alignItems="center">
        <div style={{ width: '308px', height: '308px' }}>
          {retailSalesLoading || retailSalesFromPromotionsLoading ? (
            <DonutChartLoading />
          ) : (
            <CircularProgressPercentage
              percentage={percentage}
              customLabel={
                <Flex gap="sm" alignItems="center" flexDirection="column">
                  <Text
                    sx={{
                      fontSize: '48px',
                      fontWeight: 'bold'
                    }}
                  >
                    {`${formatMetric(percentage, METRICTYPE.PERCENT, {})}`}
                  </Text>
                  <Text
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Share of Total Sales
                  </Text>
                </Flex>
              }
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <AtlasV2ComparableSplineChart
            indexName="promotions"
            fieldName="dataPointCount"
            showTitleSummary={false}
            splineChartLoadingProps={{
              width: 664
            }}
            chartOptionsOverride={{
              chart: {
                height: 280,
                width: 664
              }
            }}
            requestBuilder={(builder) => builder.addConditionRangeFilter('retailSales', 0.1)}
            shouldSliceByWeekRange
            beaconChartWithLegendOverride={(props, values) => {
              const { primarySlicedData, secondarySlicedData, secondaryTotal } = values;

              const lastPrimaryValue = primarySlicedData[primarySlicedData.length - 1];
              const lastSecondaryValue = secondarySlicedData[secondarySlicedData.length - 1];
              const primaryValueSum =
                mainTimePeriodId === '1w'
                  ? lastPrimaryValue[1]
                  : primarySlicedData.reduce((acc, curr) => (acc += curr[1]), 0);

              // Use the common utility function for consistency
              const percentChangeValue = calculatePromotionsPercentChange(
                primarySlicedData,
                secondarySlicedData,
                comparisonPeriod,
                secondaryTotal,
                mainTimePeriodId
              );

              let secondaryValueSum: number;

              if (mainTimePeriodId === '1w') {
                secondaryValueSum = lastSecondaryValue[1];
              } else if (comparisonPeriod === ComparisonPeriod.PRIOR_YEAR) {
                secondaryValueSum = secondarySlicedData
                  .filter((secondaryElement) =>
                    primarySlicedData.some((primaryElement) => primaryElement[0] === secondaryElement[0])
                  )
                  .reduce((acc, curr) => (acc += curr[1]), 0);
              } else {
                secondaryValueSum = secondaryTotal;
              }

              const propsOverride = {
                ...props,
                primaryLegendProps: {
                  ...props.primaryLegendProps,
                  metric: `${formatMetric(primaryValueSum, METRICTYPE.VOLUME, {
                    decimalPlaces: 2,
                    showFullValue: false
                  })}`,
                  change: formatMetric(Math.abs(percentChangeValue), METRICTYPE.PERCENT, {
                    showFullValue: true
                  })
                },
                comparisonLegendProps: {
                  ...props.comparisonLegendProps,
                  metric: `${formatMetric(secondaryValueSum, METRICTYPE.VOLUME, {
                    decimalPlaces: 2,
                    showFullValue: false
                  })}`
                }
              };

              return propsOverride;
            }}
          />
        </div>
      </Grid>
    </BeaconChartWithLegend>
  );
};
